import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AddRounded as AddRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  DeleteForeverOutlined as DeleteForeverOutlinedIcon,
  DeleteOutlineRounded as DeleteOutlineRoundedIcon,
  DownloadRounded as DownloadRoundedIcon,
  EditOutlined as EditOutlinedIcon,
  InsertChartRounded as InsertChartRoundedIcon,
  RefreshRounded as RefreshRoundedIcon,
  SearchRounded as SearchRoundedIcon,
  SensorsRounded as SensorsRoundedIcon,
  TimelineRounded as TimelineRoundedIcon,
  UploadFileRounded as UploadFileRoundedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from "@mui/icons-material";
import FlexibleStatsRow from "../common/FlexibleStatsRow";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import * as XLSX from "xlsx";

import { useStatus } from "../../popup/NotificationContext";
import {
  CUSTOM_ROWS_PER_PAGE_OPTIONS,
  getDisplayRange,
  getPaginatedRows,
  getRowsPerPageLabel,
  getTotalPagesForRows,
  ROWS_PER_PAGE_MENU_PROPS,
} from "../../utils/tablePagination";
import {
  useAddSensorMutation,
  useDeleteSensorMutation,
  useGetSensorsQuery,
  useUpdateSensorMutation,
} from "../../redux/logistics/logisticsApi";

const SENSOR_TYPES = ["Fuel", "Temperature", "Load", "Door", "Other"];
const PAGE_SECTIONS = [
  { key: "list", label: "Sensor List" },
  { key: "create", label: "Add Sensor" },
  { key: "upload", label: "Upload Sensors" },
];
const CHART_COLORS = ["#2563eb", "#22c55e", "#f59e0b", "#fb7185", "#a855f7", "#94a3b8"];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#fff",
    minHeight: 52,
  },
};

const softCardSx = {
  borderRadius: 4,
  border: "1px solid rgba(148,163,184,0.16)",
  boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
  background: "#ffffff",
};

const toNumberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeCalibration = (calibration) => {
  if (!Array.isArray(calibration)) return [];
  return calibration
    .map((row) => ({
      value: row?.value ?? row?.fuel ?? "",
      voltage: row?.voltage ?? "",
    }))
    .map((row) => ({
      value: row.value === 0 ? 0 : row.value || "",
      voltage: row.voltage === 0 ? 0 : row.voltage || "",
    }));
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB");
};

const getStatusMeta = (sensor) => {
  const statusValue = String(sensor?.status || "").toLowerCase();
  if (statusValue === "inactive" || sensor?.active === false || sensor?.isActive === false) {
    return { label: "Inactive", bg: "#fee2e2", fg: "#ef4444" };
  }
  return { label: "Active", bg: "#dcfce7", fg: "#16a34a" };
};

const createTemplateFile = () => {
  const rows = [
    {
      Name: "Fuel Tank Sensor",
      "Sensor Type": "Fuel",
      Size: "Medium",
      Fuel: "10",
      Voltage: "2.5",
      Status: "Active",
    },
  ];
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sensors");
  return XLSX.write(workbook, { bookType: "xlsx", type: "array" });
};

const getUploadStatusMeta = (valid) =>
  valid ? { bg: "#dcfce7", fg: "#166534", label: "Valid" } : { bg: "#fee2e2", fg: "#b91c1c", label: "Invalid" };

const initialFormState = () => ({
  name: "",
  type: "Fuel",
  size: "",
  calibration: [{ value: "", voltage: "" }],
});

const PAGE_META = {
  list: {
    title: "Sensors Overview",
    subtitle: "Home > Sensors > Sensor List",
  },
  create: {
    title: "Add Sensor",
    subtitle: "Home > Sensors > Add Sensor",
  },
  upload: {
    title: "Upload Sensors",
    subtitle: "Home > Sensors > Upload Sensors",
  },
};

export default function SensorPortalModal({ open = true, onClose, asPage = false, section = "list" }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const importFileRef = useRef(null);
  const { showStatus } = useStatus();
  const { data: sensorsPayload = [], refetch } = useGetSensorsQuery(undefined, { skip: !asPage && !open });
  const [addSensor, { isLoading: isAdding }] = useAddSensorMutation();
  const [updateSensor, { isLoading: isUpdating }] = useUpdateSensorMutation();
  const [deleteSensor, { isLoading: isDeleting }] = useDeleteSensorMutation();

  const [activeSection, setActiveSection] = useState(section);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [editingSensor, setEditingSensor] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState([]);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    setActiveSection(section);
  }, [section]);

  const sensors = useMemo(() => {
    const list = Array.isArray(sensorsPayload) ? sensorsPayload : sensorsPayload?.data || [];
    return Array.isArray(list) ? list : [];
  }, [sensorsPayload]);

  const normalizedSensors = useMemo(
    () =>
      sensors.map((sensor) => {
        const calibration = normalizeCalibration(sensor?.calibration);
        return {
          raw: sensor,
          id: sensor?._id || sensor?.id,
          name: sensor?.name || "-",
          type: sensor?.type || "-",
          size: sensor?.size || "-",
          calibration,
          calibrationCount: calibration.length,
          status: getStatusMeta(sensor),
          lastCalibrated: sensor?.lastCalibrated || sensor?.updatedAt || sensor?.createdAt || "",
          createdAt: sensor?.createdAt || "",
        };
      }),
    [sensors]
  );

  const filteredSensors = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return normalizedSensors.filter((item) => {
      const matchesSearch =
        !query ||
        [item.name, item.type, item.size].some((value) => String(value || "").toLowerCase().includes(query));
      const matchesType = typeFilter === "all" || item.type === typeFilter;
      const matchesStatus = statusFilter === "all" || item.status.label.toLowerCase() === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [normalizedSensors, searchQuery, typeFilter, statusFilter]);

  const totalPages = getTotalPagesForRows(filteredSensors.length, rowsPerPage);
  const pagedSensors = useMemo(() => getPaginatedRows(filteredSensors, page, rowsPerPage), [filteredSensors, page, rowsPerPage]);
  const displayRange = useMemo(() => getDisplayRange(filteredSensors.length, page, rowsPerPage), [filteredSensors.length, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  useEffect(() => {
    if (editingSensor) {
      const raw = editingSensor.raw || editingSensor;
      const calibration = normalizeCalibration(raw?.calibration);
      setForm({
        name: String(raw?.name || ""),
        type: String(raw?.type || "Fuel"),
        size: String(raw?.size || ""),
        calibration: calibration.length ? calibration : [{ value: "", voltage: "" }],
      });
      setActiveSection("create");
    }
  }, [editingSensor]);

  const stats = useMemo(() => {
    const activeCount = normalizedSensors.filter((sensor) => sensor.status.label === "Active").length;
    const inactiveCount = normalizedSensors.length - activeCount;
    const totalCalibrations = normalizedSensors.reduce((sum, sensor) => sum + sensor.calibrationCount, 0);
    return {
      total: normalizedSensors.length,
      active: activeCount,
      inactive: inactiveCount,
      totalCalibrations,
    };
  }, [normalizedSensors]);

  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total Sensors",
        value: stats.total,
        detail: "All sensors",
        tone: "neutral",
        icon: <SensorsRoundedIcon fontSize="small" />,
      },
      {
        key: "active",
        label: "Active Sensors",
        value: stats.active,
        detail: "Currently active",
        tone: "success",
        icon: <SensorsRoundedIcon fontSize="small" />,
      },
      {
        key: "inactive",
        label: "Inactive Sensors",
        value: stats.inactive,
        detail: "Currently inactive",
        tone: "warning",
        icon: <SensorsRoundedIcon fontSize="small" />,
      },
      {
        key: "calibrations",
        label: "Total Calibrations",
        value: stats.totalCalibrations,
        detail: "All time",
        tone: "violet",
        icon: <TimelineRoundedIcon fontSize="small" />,
      },
    ],
    [stats]
  );

  const trendData = useMemo(() => {
    const bucket = new Map();
    normalizedSensors.forEach((sensor) => {
      if (!sensor.createdAt) return;
      const date = new Date(sensor.createdAt);
      if (Number.isNaN(date.getTime())) return;
      const key = date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      bucket.set(key, (bucket.get(key) || 0) + 1);
    });
    return Array.from(bucket.entries()).map(([name, count]) => ({ name, count }));
  }, [normalizedSensors]);

  const typeChartData = useMemo(() => {
    const bucket = new Map();
    normalizedSensors.forEach((sensor) => {
      const key = sensor.type || "Other";
      bucket.set(key, (bucket.get(key) || 0) + 1);
    });
    return Array.from(bucket.entries()).map(([name, value]) => ({ name, value }));
  }, [normalizedSensors]);

  const uploadStats = useMemo(() => {
    const valid = uploadPreview.filter((row) => row.isValid).length;
    return {
      total: uploadPreview.length,
      valid,
      invalid: uploadPreview.length - valid,
    };
  }, [uploadPreview]);

  const uploadSummaryCards = useMemo(
    () => [
      {
        key: "totalRows",
        label: "Total Rows",
        value: uploadStats.total,
        detail: "Preview loaded",
        tone: "neutral",
        icon: <SensorsRoundedIcon fontSize="small" />,
      },
      {
        key: "validRows",
        label: "Valid Rows",
        value: uploadStats.valid,
        detail: "Ready to import",
        tone: "success",
        icon: <UploadFileRoundedIcon fontSize="small" />,
      },
      {
        key: "invalidRows",
        label: "Invalid Rows",
        value: uploadStats.invalid,
        detail: "Needs correction",
        tone: "danger",
        icon: <CloseRoundedIcon fontSize="small" />,
      },
    ],
    [uploadStats]
  );

  const busy = isAdding || isUpdating || isDeleting || importing;
  const meta = PAGE_META[activeSection] || PAGE_META.list;

  const resetForm = () => {
    setForm(initialFormState());
    setEditingSensor(null);
  };

  const addCalibrationRow = () => {
    setForm((prev) => ({
      ...prev,
      calibration: [...prev.calibration, { value: "", voltage: "" }],
    }));
  };

  const removeCalibrationRow = (index) => {
    setForm((prev) => {
      const next = prev.calibration.filter((_, idx) => idx !== index);
      return { ...prev, calibration: next.length ? next : [{ value: "", voltage: "" }] };
    });
  };

  const updateCalibrationRow = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      calibration: prev.calibration.map((row, idx) => (idx === index ? { ...row, [key]: value } : row)),
    }));
  };

  const buildSensorPayload = (values) => ({
    name: values.name.trim(),
    type: values.type,
    size: values.size,
    calibration: (Array.isArray(values.calibration) ? values.calibration : [])
      .map((row) => ({
        value: toNumberOrNull(row.value),
        voltage: toNumberOrNull(row.voltage),
      }))
      .filter((row) => row.value != null && row.voltage != null),
  });

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showStatus("warning", "Name is required.");
      return;
    }

    const payload = buildSensorPayload(form);

    try {
      if (editingSensor?.id) {
        await updateSensor({ id: editingSensor.id, ...payload }).unwrap();
        showStatus("success", "Sensor updated.");
      } else {
        await addSensor(payload).unwrap();
        showStatus("success", "Sensor added.");
      }
      await refetch();
      resetForm();
      if (!asPage) setActiveSection("list");
    } catch (error) {
      showStatus("error", error?.data?.error || error?.data?.msg || "Sensor save failed.");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteSensor(deleteTarget.id).unwrap();
      showStatus("success", "Sensor deleted.");
      if (selectedSensor?.id === deleteTarget.id) setSelectedSensor(null);
      await refetch();
      setDeleteTarget(null);
    } catch (error) {
      showStatus("error", error?.data?.error || "Failed to delete sensor.");
    }
  };

  const downloadCsv = () => {
    const headers = ["Name", "Sensor Type", "Size", "Calibration Count", "Status", "Last Calibrated", "Created At"];
    const lines = filteredSensors.map((sensor) =>
      [
        sensor.name,
        sensor.type,
        sensor.size,
        sensor.calibrationCount,
        sensor.status.label,
        formatDate(sensor.lastCalibrated),
        formatDate(sensor.createdAt),
      ]
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[headers.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sensors.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showStatus("success", "Sensors CSV downloaded.");
  };

  const handleDownloadTemplate = () => {
    const content = createTemplateFile();
    const blob = new Blob([content], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sensor-upload-template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const mapUploadRows = (rows) =>
    rows.map((row, index) => {
      const name = row.Name || row.name || "";
      const type = row["Sensor Type"] || row.type || "";
      const size = row.Size || row.size || "";
      const fuel = row.Fuel || row.fuel || "";
      const voltage = row.Voltage || row.voltage || "";
      const isTypeValid = SENSOR_TYPES.some((item) => item.toLowerCase() === String(type).toLowerCase());
      const errors = [];
      if (!name) errors.push("Name missing");
      if (!isTypeValid) errors.push("Invalid sensor type");
      const calibration = fuel !== "" && voltage !== "" ? [{ value: fuel, voltage }] : [];
      return {
        id: `${index}-${name}-${type}`,
        name: String(name || ""),
        type: String(type || ""),
        size: String(size || ""),
        calibration,
        isValid: errors.length === 0,
        errors,
      };
    });

  const handleUploadFile = async (file) => {
    if (!file) return;
    setUploadFile(file);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      setUploadPreview(mapUploadRows(rows));
    } catch (error) {
      console.error(error);
      showStatus("error", "Failed to parse uploaded file.");
    }
  };

  const handleImportSensors = async () => {
    const validRows = uploadPreview.filter((row) => row.isValid);
    if (!validRows.length) {
      showStatus("warning", "No valid sensor rows available.");
      return;
    }

    setImporting(true);
    try {
      for (const row of validRows) {
        await addSensor(buildSensorPayload(row)).unwrap();
      }
      await refetch();
      showStatus("success", `${validRows.length} sensors imported.`);
      setUploadFile(null);
      setUploadPreview([]);
    } catch (error) {
      console.error(error);
      showStatus("error", error?.data?.error || "Failed to import sensors.");
    } finally {
      setImporting(false);
    }
  };

  const renderRowActions = (sensor) => (
    <Stack direction="row" spacing={0.8}>
      <Tooltip title="View">
        <IconButton
          onClick={() => setSelectedSensor(sensor)}
          sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: "#dbeafe", color: "#2563eb" }}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton
          onClick={() => setEditingSensor(sensor)}
          sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: "#dbeafe", color: "#2563eb" }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          onClick={() => setDeleteTarget(sensor)}
          sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: "#fee2e2", color: "#ef4444" }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderListPage = () => (
    <Stack spacing={2.5}>
      <FlexibleStatsRow items={summaryCards} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" }, gap: 1.4 }}>
        <Paper sx={{ ...softCardSx, p: { xs: 2, lg: 2.1 } }}>
          <Stack spacing={1.8}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>Sensors Added Over Time</Typography>
              <Chip label="This Month" sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700 }} />
            </Stack>
            <Box sx={{ height: { xs: 240, lg: 208 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="sensorArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.26} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={3} fill="url(#sensorArea)" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ ...softCardSx, p: { xs: 2, lg: 2.1 } }}>
          <Stack spacing={1.8}>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>Sensors by Type</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" }, gap: 1.5, alignItems: "center" }}>
              <Box sx={{ height: { xs: 240, lg: 208 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={typeChartData} dataKey="value" nameKey="name" innerRadius={74} outerRadius={112}>
                      {typeChartData.map((entry, index) => (
                        <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={1.2}>
                {typeChartData.map((entry, index) => (
                  <Stack key={entry.name} direction="row" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: CHART_COLORS[index % CHART_COLORS.length] }} />
                      <Typography sx={{ color: "#475569", fontWeight: 600 }}>{entry.name}</Typography>
                    </Stack>
                    <Typography sx={{ color: "#0f172a", fontWeight: 800 }}>{entry.value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Paper sx={{ ...softCardSx, p: 2.5 }}>
        <Stack spacing={2}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr 1fr auto auto auto auto" }, gap: 1.4, alignItems: "center" }}>
            <TextField
              placeholder="Search by name or type..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={inputSx}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(event) => setTypeFilter(event.target.value)}>
                <MenuItem value="all">All Types</MenuItem>
                {SENSOR_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={inputSx}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(event) => setStatusFilter(event.target.value)}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} sx={{ width: 48, height: 48, borderRadius: 3, border: "1px solid rgba(148,163,184,0.24)" }}>
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Charts">
              <IconButton onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ width: 48, height: 48, borderRadius: 3, border: "1px solid rgba(148,163,184,0.24)" }}>
                <InsertChartRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton onClick={downloadCsv} sx={{ width: 48, height: 48, borderRadius: 3, border: "1px solid rgba(148,163,184,0.24)" }}>
                <DownloadRoundedIcon />
              </IconButton>
            </Tooltip>
            <FormControl sx={{ minWidth: 88 }}>
              <Select value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }} MenuProps={ROWS_PER_PAGE_MENU_PROPS}>
                {CUSTOM_ROWS_PER_PAGE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {getRowsPerPageLabel(size)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isMobile ? (
            <Stack spacing={1.3}>
              {pagedSensors.length ? (
                pagedSensors.map((sensor) => (
                  <Paper key={sensor.id} sx={{ p: 1.8, borderRadius: 3, border: "1px solid rgba(148,163,184,0.16)" }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>{sensor.name}</Typography>
                        <Chip label={sensor.status.label} size="small" sx={{ bgcolor: sensor.status.bg, color: sensor.status.fg, fontWeight: 700 }} />
                      </Stack>
                      <Typography sx={{ color: "#475569", fontSize: 13.5 }}>Type: {sensor.type}</Typography>
                      <Typography sx={{ color: "#475569", fontSize: 13.5 }}>Size: {sensor.size}</Typography>
                      <Typography sx={{ color: "#475569", fontSize: 13.5 }}>Calibrations: {sensor.calibrationCount}</Typography>
                      <Typography sx={{ color: "#475569", fontSize: 13.5 }}>Last Calibrated: {formatDate(sensor.lastCalibrated)}</Typography>
                      {renderRowActions(sensor)}
                    </Stack>
                  </Paper>
                ))
              ) : (
                <EmptyState onCreate={() => setActiveSection("create")} />
              )}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Name", "Sensor Type", "Size", "Calibration Count", "Status", "Last Calibrated", "Created At", "Actions"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: 700, color: "#64748b", borderBottom: "1px solid rgba(148,163,184,0.16)" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedSensors.length ? (
                    pagedSensors.map((sensor) => (
                      <TableRow key={sensor.id} hover>
                        <TableCell sx={{ fontWeight: 800 }}>{sensor.name}</TableCell>
                        <TableCell>{sensor.type}</TableCell>
                        <TableCell>{sensor.size}</TableCell>
                        <TableCell>{sensor.calibrationCount}</TableCell>
                        <TableCell>
                          <Chip label={sensor.status.label} size="small" sx={{ bgcolor: sensor.status.bg, color: sensor.status.fg, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>{formatDate(sensor.lastCalibrated)}</TableCell>
                        <TableCell>{formatDate(sensor.createdAt)}</TableCell>
                        <TableCell>{renderRowActions(sensor)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <EmptyState onCreate={() => setActiveSection("create")} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap">
            <Typography sx={{ color: "#475569", fontSize: 14 }}>
              Showing {displayRange.from} to {displayRange.to} of {filteredSensors.length} entries
            </Typography>
            <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );

  const renderCreatePage = () => (
    <Paper sx={{ ...softCardSx, p: { xs: 2, md: 2.5 } }}>
      <Stack spacing={2.5}>
        <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a" }}>
          {editingSensor ? "Edit Sensor" : "Add Sensor"}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            sx={inputSx}
          />
          <FormControl sx={inputSx}>
            <InputLabel>Sensor Type</InputLabel>
            <Select value={form.type} label="Sensor Type" onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}>
              {SENSOR_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Size"
            value={form.size}
            onChange={(event) => setForm((prev) => ({ ...prev, size: event.target.value }))}
            sx={inputSx}
          />
        </Box>

        <Paper sx={{ ...softCardSx, p: 2.5, bgcolor: "#fafcff" }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>Fuel Calibration</Typography>
              <Button onClick={addCalibrationRow} startIcon={<AddRoundedIcon />} sx={{ textTransform: "none", fontWeight: 800 }}>
                Add calibration row
              </Button>
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 60px" }, gap: 2 }}>
              {form.calibration.map((row, idx) => (
                <React.Fragment key={`cal-${idx}`}>
                  <TextField
                    label="Fuel"
                    type="number"
                    value={row.value}
                    onChange={(event) => updateCalibrationRow(idx, "value", event.target.value)}
                    sx={inputSx}
                  />
                  <TextField
                    label="Voltage"
                    type="number"
                    value={row.voltage}
                    onChange={(event) => updateCalibrationRow(idx, "voltage", event.target.value)}
                    sx={inputSx}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconButton onClick={() => removeCalibrationRow(idx)} disabled={form.calibration.length <= 1} sx={{ color: "#ef4444" }}>
                      <DeleteForeverOutlinedIcon />
                    </IconButton>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" onClick={handleSubmit} disabled={busy} sx={{ borderRadius: 3, px: 2.5, py: 1.2, textTransform: "none", fontWeight: 800 }}>
            {busy ? "Submitting..." : editingSensor ? "Update Sensor" : "Submit"}
          </Button>
          <Button variant="outlined" onClick={() => { resetForm(); if (!asPage) setActiveSection("list"); }} sx={{ borderRadius: 3, px: 2.5, py: 1.2, textTransform: "none", fontWeight: 800 }}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  const renderUploadPage = () => (
    <Stack spacing={2.5}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.45fr 0.95fr" }, gap: 2.5 }}>
        <Paper sx={{ ...softCardSx, p: 2.5 }}>
          <Stack spacing={2}>
            <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a" }}>Upload File</Typography>
            <Paper
              variant="outlined"
              onClick={() => importFileRef.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                handleUploadFile(event.dataTransfer.files?.[0]);
              }}
              sx={{
                p: 3,
                borderRadius: 3,
                borderStyle: "dashed",
                borderColor: "rgba(37,99,235,0.25)",
                background: "#fbfdff",
                cursor: "pointer",
              }}
            >
              <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: "#dcfce7", color: "#16a34a" }}>
                  <UploadFileRoundedIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>
                  {uploadFile ? uploadFile.name : "Drag & drop your file here"}
                </Typography>
                <Typography sx={{ color: "#64748b", textAlign: "center" }}>
                  Supports: .csv, .xlsx, .xls (Max. 5MB)
                </Typography>
                <Button variant="contained" sx={{ borderRadius: 3, textTransform: "none", fontWeight: 800 }}>
                  Choose File
                </Button>
              </Stack>
            </Paper>
            <input
              ref={importFileRef}
              type="file"
              hidden
              accept=".csv,.xlsx,.xls"
              onChange={(event) => handleUploadFile(event.target.files?.[0])}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
              <Button startIcon={<DownloadRoundedIcon />} variant="text" onClick={handleDownloadTemplate} sx={{ textTransform: "none", fontWeight: 800 }}>
                Download sample template
              </Button>
              <Button startIcon={<DownloadRoundedIcon />} variant="outlined" onClick={handleDownloadTemplate} sx={{ textTransform: "none", fontWeight: 800, borderRadius: 3 }}>
                Download Template
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack spacing={2.5}>
          <Paper sx={{ ...softCardSx, p: 2.5, background: "linear-gradient(180deg,#f6fff9 0%, #ffffff 100%)" }}>
            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>File Guidelines</Typography>
              {[
                "File must be in .csv, .xlsx or .xls format",
                "Maximum file size: 5MB",
                "First row must contain column headers",
                "Supported calibration columns: Fuel and Voltage",
              ].map((item) => (
                <Stack key={item} direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: "#16a34a" }} />
                  <Typography sx={{ color: "#475569" }}>{item}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ ...softCardSx, p: 2.5 }}>
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>Template Guidelines</Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {[
                      ["Name", "Fuel Tank Sensor"],
                      ["Sensor Type", "Fuel / Temperature / Load / Door / Other"],
                      ["Size", "Medium"],
                      ["Fuel", "10"],
                      ["Voltage", "2.5"],
                    ].map(([label, value]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: 700 }}>{label}</TableCell>
                        <TableCell sx={{ color: "#64748b" }}>{value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {uploadStats.invalid ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Invalid rows will not be imported. Please fix the errors and try again.
                </Alert>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <FlexibleStatsRow items={uploadSummaryCards} />

      <Paper sx={{ ...softCardSx, p: 2.5 }}>
        <Stack spacing={2}>
          <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>Upload Preview</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {["Name", "Sensor Type", "Size", "Calibration", "Status"].map((head) => (
                    <TableCell key={head} sx={{ fontWeight: 800, color: "#64748b" }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadPreview.length ? (
                  uploadPreview.map((row) => {
                    const metaStatus = getUploadStatusMeta(row.isValid);
                    return (
                      <TableRow key={row.id} sx={{ bgcolor: row.isValid ? "#f8fffb" : "#fff7f7" }}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>{row.size || "-"}</TableCell>
                        <TableCell>{row.calibration.length ? `${row.calibration.length} row` : "-"}</TableCell>
                        <TableCell>
                          <Stack spacing={0.4} alignItems="flex-start">
                            <Chip label={metaStatus.label} size="small" sx={{ bgcolor: metaStatus.bg, color: metaStatus.fg, fontWeight: 700 }} />
                            {!row.isValid ? (
                              <Typography sx={{ color: "#b91c1c", fontSize: 12.5 }}>
                                {row.errors.join(", ")}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8, color: "#64748b" }}>
                      Upload a CSV or Excel file to preview rows here.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="space-between" flexWrap="wrap" spacing={2}>
            <Button variant="outlined" onClick={() => { setUploadFile(null); setUploadPreview([]); }} sx={{ borderRadius: 3, px: 2.5, textTransform: "none", fontWeight: 800 }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleImportSensors} disabled={!uploadStats.valid || busy} sx={{ borderRadius: 3, px: 2.5, textTransform: "none", fontWeight: 800 }}>
              {busy ? "Importing..." : "Import Sensors"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );

  const content = activeSection === "create" ? renderCreatePage() : activeSection === "upload" ? renderUploadPage() : renderListPage();

  const shell = (
    <Stack spacing={2.5}>
      {!asPage ? (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {PAGE_SECTIONS.map((item) => (
            <Chip
              key={item.key}
              label={item.label}
              onClick={() => setActiveSection(item.key)}
              sx={{
                borderRadius: 99,
                bgcolor: activeSection === item.key ? "#dbeafe" : "#f8fafc",
                color: activeSection === item.key ? "#1d4ed8" : "#475569",
                fontWeight: 800,
              }}
            />
          ))}
        </Stack>
      ) : null}

      {content}

      <Dialog open={Boolean(selectedSensor)} onClose={() => setSelectedSensor(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Sensor Details</DialogTitle>
        <DialogContent dividers>
          {selectedSensor ? (
            <Stack spacing={1.4}>
              <Typography><strong>Name:</strong> {selectedSensor.name}</Typography>
              <Typography><strong>Type:</strong> {selectedSensor.type}</Typography>
              <Typography><strong>Size:</strong> {selectedSensor.size}</Typography>
              <Typography><strong>Status:</strong> {selectedSensor.status.label}</Typography>
              <Typography><strong>Last Calibrated:</strong> {formatDate(selectedSensor.lastCalibrated)}</Typography>
              <Typography sx={{ fontWeight: 800 }}>Calibration</Typography>
              {selectedSensor.calibration.length ? (
                selectedSensor.calibration.map((row, idx) => (
                  <Typography key={`${selectedSensor.id}-cal-${idx}`}>
                    Fuel: {row.value} | Voltage: {row.voltage}
                  </Typography>
                ))
              ) : (
                <Typography>No calibration points</Typography>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedSensor(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Sensor</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: "#475569" }}>
            Are you sure you want to delete this sensor? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={busy}>
            {busy ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );

  if (asPage) return shell;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", minHeight: "84vh" } }}>
      <DialogTitle sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>
            {meta.title}
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 14.5 }}>{meta.subtitle}</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>
        {shell}
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ onCreate }) {
  return (
    <Stack spacing={1} alignItems="center" sx={{ py: 9, color: "#6b7280" }}>
      <Avatar sx={{ width: 92, height: 92, bgcolor: "#eef2ff", color: "#2563eb" }}>
        <SensorsRoundedIcon sx={{ fontSize: 42 }} />
      </Avatar>
      <Typography sx={{ fontWeight: 800, fontSize: 22, color: "#0f172a" }}>No sensors found</Typography>
      <Typography sx={{ textAlign: "center", maxWidth: 360 }}>
        Add your first sensor to start calibration, monitoring, and assignment workflows.
      </Typography>
      {onCreate ? (
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={onCreate} sx={{ mt: 1, borderRadius: 3, textTransform: "none", fontWeight: 800 }}>
          Add Sensor
        </Button>
      ) : null}
    </Stack>
  );
}
